import type { RouteObject } from 'react-router-dom';
import Spinner from '~/components/Spinner';
import {
  catchallRoute,
  makeRegistrationLoader,
  redirect,
} from '~/utils/routing';
import ErrorPage from './error';
import EditAddressPage from './features/address/edit/EditAddressPage';
import {
  action as editAddressAction,
  loader as editAddressLoader,
} from './features/address/edit/editAddress';
import AddressReviewPage from './features/address/review/AddressReviewPage';
import { loader as addressReviewLoader } from './features/address/review/addressReview';
import AppShellLayout from './features/app-shell/AppShellLayout';
import { loader as appShellLoader } from './features/app-shell/appShell';
import DraftClaimLineItemLayout from './features/crew/claim-line-items/draft-layout/DraftClaimLineItemLayout';
import {
  loader as draftClaimLineItemLayoutLoader,
  shouldRevalidate as draftClaimLineItemLayoutShouldRevalidate,
} from './features/crew/claim-line-items/draft-layout/draftClaimLineItem';
import ClaimLineItemMetaPage from './features/crew/claim-line-items/meta/ClaimLineItemMetaPage';
import {
  action as claimLineItemMetaAction,
  loader as claimLineItemMetaLoader,
} from './features/crew/claim-line-items/meta/claimLineItemMeta';
import QuantityPage from './features/crew/claim-line-items/quantity/QuantityPage';
import {
  action as quantityAction,
  loader as quantityLoader,
} from './features/crew/claim-line-items/quantity/quantity';
import ReasonDetailPage from './features/crew/claim-line-items/reason-detail/ReasonDetailPage';
import {
  action as reasonDetailAction,
  loader as reasonDetailLoader,
} from './features/crew/claim-line-items/reason-detail/reasonDetail';
import ReasonFollowUpPage from './features/crew/claim-line-items/reason-follow-up/ReasonFollowUpPage';
import {
  action as reasonFollowUpAction,
  loader as reasonFollowUpLoader,
  shouldRevalidate as reasonFollowUpShouldRevalidate,
} from './features/crew/claim-line-items/reason-follow-up/reasonFollowUp';
import ClaimLineItemReasonPage from './features/crew/claim-line-items/reason/ClaimLineItemReasonPage';
import {
  action as claimLineItemReasonAction,
  loader as claimLineItemReasonLoader,
} from './features/crew/claim-line-items/reason/claimLineItemReason';
import ResolutionPage from './features/crew/claim-line-items/resolution/ResolutionPage';
import {
  action as resolutionAction,
  loader as resolutionLoader,
} from './features/crew/claim-line-items/resolution/resolution';
import ShippingProtectionPage from './features/crew/claim-line-items/shipping-protection/ShippingProtectionPage';
import { loader as shippingProtectionLoader } from './features/crew/claim-line-items/shipping-protection/shippingProtection';
import ClaimLineItemsSummaryPage from './features/crew/claim-line-items/summary/ClaimLineItemsSummaryPage';
import {
  action as claimLineItemsSummaryAction,
  loader as claimLineItemsSummaryLoader,
} from './features/crew/claim-line-items/summary/claimLineItemsSummary';
import ClaimOverviewPage from './features/crew/claim-overview/ClaimOverviewPage';
import { loader as claimOverviewLoader } from './features/crew/claim-overview/claimOverview';
import NewRequestPage from './features/crew/new-request/NewRequestPage';
import {
  action as newRequestAction,
  loader as newRequestLoader,
} from './features/crew/new-request/newRequest';
import OrderHubPage from './features/crew/order-hub/OrderHubPage';
import { loader as orderHubLoader } from './features/crew/order-hub/orderHub';
import { middleware as crewOrderMiddleware } from './features/crew/order-middleware/OrderMiddleware';
import ReturnMethodsPage from './features/crew/return-methods/ReturnMethodsPage';
import {
  action as returnMethodsAction,
  loader as returnMethodsLoader,
} from './features/crew/return-methods/returnMethods';
import ReturnSummaryPage from './features/crew/return-summary/ReturnSummaryPage';
import {
  action as returnSummaryAction,
  loader as returnSummaryLoader,
} from './features/crew/return-summary/returnSummary';
import WarrantyReviewPage from './features/crew/warranty-review/WarrantyReviewPage';
import {
  action as warrantyReviewAction,
  loader as warrantyReviewLoader,
} from './features/crew/warranty-review/warrantyReview';
import InactiveMerchantPage from './features/inactive-merchant/InactiveMerchantPage';
import {
  BasicOrderLookupPage,
  BrandedOrderLookupPage,
} from './features/order-lookup/OrderLookupPage';
import {
  crewOrderLookupAction,
  crewOrderLookupLoader,
  orderLookupAction,
} from './features/order-lookup/orderLookup';
import { middleware as protectionOrderMiddleware } from './features/protection-order/middleware';
import ProtectionOrderNotProtectedPage from './features/protection-order/not-protected/ProtectionOrderNotProtectedPage';
import ProtectionOrderOverviewPage from './features/protection-order/overview/ProtectionOrderOverviewPage';
import { loader as protectionOrderOverviewLoader } from './features/protection-order/overview/protectionOrderOverview';
import CustomerInfoPage from './features/registrations/customer-info/CustomerInfoPage';
import {
  action as customerInfoAction,
  loader as customerInfoLoader,
} from './features/registrations/customer-info/customerInfo';
import RegistrationOverviewPage from './features/registrations/registration-overview/RegistrationOverviewPage';
import { loader as registrationLoader } from './features/registrations/registration-overview/registrationOverview';
import SelectProductsPage from './features/registrations/select-products/SelectProductsPage';
import {
  action as selectProductsAction,
  loader as selectProductsLoader,
} from './features/registrations/select-products/selectProducts';
import ShippingProtectionClaimOverviewPage from './features/shipping-claim/claim-overview/ShippingClaimOverviewPage';
import { loader as shippingProtectionClaimOverviewLoader } from './features/shipping-claim/claim-overview/shippingClaimOverview';
import ShippingClaimItemsPage from './features/shipping-claim/new-claim/items/ShippingClaimItemsPage';
import {
  action as shippingClaimItemsAction,
  loader as shippingClaimItemsLoader,
} from './features/shipping-claim/new-claim/items/shippingClaimItems';
import ShippingClaimMetaPage from './features/shipping-claim/new-claim/meta/ShippingClaimMetaPage';
import {
  action as shippingClaimMetaAction,
  loader as shippingClaimMetaLoader,
} from './features/shipping-claim/new-claim/meta/shippingClaimMeta';
import ShippingClaimReasonPage from './features/shipping-claim/new-claim/reason/ShippingClaimReasonPage';
import {
  action as shippingClaimReasonAction,
  loader as shippingClaimReasonLoader,
} from './features/shipping-claim/new-claim/reason/shippingClaimReason';
import ShippingClaimReviewPage from './features/shipping-claim/new-claim/review/ShippingClaimReviewPage';
import {
  action as shippingClaimReviewAction,
  loader as shippingClaimReviewLoader,
} from './features/shipping-claim/new-claim/review/shippingClaimReview';
import ShippingIssuePage from './features/shipping-claim/new-claim/shipping-issue/ShippingIssuePage';
import { loader as shippingIssueLoader } from './features/shipping-claim/new-claim/shipping-issue/shippingIssue';
import { middleware } from './middleware';

export default {
  id: 'appShell',
  HydrateFallback: Spinner,
  handle: {
    middleware,
  },
  loader: appShellLoader,
  ErrorBoundary: ErrorPage,
  Component: AppShellLayout,
  children: [
    {
      id: 'orderLookup',
      index: true,
      action: orderLookupAction,
      Component: BasicOrderLookupPage,
    },
    // Crew routes
    {
      id: 'crew',
      path: '/:store',
      children: [
        {
          id: 'crewOrderLookup',
          index: true,
          action: crewOrderLookupAction,
          loader: crewOrderLookupLoader,
          Component: BrandedOrderLookupPage,
        },
        {
          id: 'crewOrder',
          path: 'order/:idFromPlatform',
          handle: {
            middleware: crewOrderMiddleware,
          },
          children: [
            {
              id: 'orderHub',
              index: true,
              Component: OrderHubPage,
              loader: orderHubLoader,
            },
            {
              path: 'new-request',
              action: newRequestAction,
              loader: newRequestLoader,
              Component: NewRequestPage,
            },
            {
              id: 'crewClaim',
              path: ':claimType',
              children: [
                {
                  id: 'claimLineItemsSummary',
                  index: true,
                  loader: claimLineItemsSummaryLoader,
                  Component: ClaimLineItemsSummaryPage,
                  action: claimLineItemsSummaryAction,
                },
                {
                  id: 'draftClaimLineItemLayout',
                  path: ':draftId',
                  loader: draftClaimLineItemLayoutLoader,
                  Component: DraftClaimLineItemLayout,
                  shouldRevalidate: draftClaimLineItemLayoutShouldRevalidate,
                  children: [
                    {
                      id: 'claimLineItemQuantity',
                      index: true,
                      Component: QuantityPage,
                      action: quantityAction,
                      loader: quantityLoader,
                    },
                    {
                      id: 'claimLineItemReason',
                      path: 'reason',
                      Component: ClaimLineItemReasonPage,
                      action: claimLineItemReasonAction,
                      loader: claimLineItemReasonLoader,
                    },
                    {
                      id: 'reasonDetail',
                      // ? tempted to simplify this to just reason-detail, not sure if really useful considering the id is on state
                      path: 'reason/:reasonId',
                      Component: ReasonDetailPage,
                      loader: reasonDetailLoader,
                      action: reasonDetailAction,
                    },
                    {
                      id: 'claimLineItemMeta',
                      path: 'meta',
                      Component: ClaimLineItemMetaPage,
                      loader: claimLineItemMetaLoader,
                      action: claimLineItemMetaAction,
                    },
                    {
                      id: 'reasonFollowUp',
                      path: 'follow-up',
                      Component: ReasonFollowUpPage,
                      loader: reasonFollowUpLoader,
                      action: reasonFollowUpAction,
                      shouldRevalidate: reasonFollowUpShouldRevalidate,
                    },
                    {
                      id: 'shippingProtection',
                      path: 'shipping-protection',
                      Component: ShippingProtectionPage,
                      loader: shippingProtectionLoader,
                    },
                    {
                      id: 'resolution',
                      path: 'resolution',
                      Component: ResolutionPage,
                      action: resolutionAction,
                      loader: resolutionLoader,
                    },
                  ],
                },
                {
                  id: 'address',
                  path: 'address',
                  children: [
                    {
                      id: 'addressReview',
                      index: true,
                      loader: addressReviewLoader,
                      Component: AddressReviewPage,
                    },
                    {
                      id: 'editAddress',
                      path: 'edit',
                      action: editAddressAction,
                      loader: editAddressLoader,
                      Component: EditAddressPage,
                    },
                  ],
                },
                {
                  id: 'returnMethods',
                  path: 'return-methods',
                  action: returnMethodsAction,
                  loader: returnMethodsLoader,
                  Component: ReturnMethodsPage,
                },
                {
                  id: 'reviewWarranty',
                  path: 'warranty-review',
                  Component: WarrantyReviewPage,
                  loader: warrantyReviewLoader,
                  action: warrantyReviewAction,
                },
                {
                  id: 'returnSummary',
                  path: 'return-summary',
                  Component: ReturnSummaryPage,
                  loader: returnSummaryLoader,
                  action: returnSummaryAction,
                  // //! don't want to run the loader again after the action call -- e.g. it will create a new payment intent if the payment confirmation fails on a declined card
                  // shouldRevalidate: () => false,
                },
              ],
            },
          ],
        },
        {
          id: 'inactiveMerchant',
          path: 'inactive',
          Component: InactiveMerchantPage,
        },
        {
          id: 'claimOverview',
          path: 'claim/overview/:claimExternalId',
          Component: ClaimOverviewPage,
          loader: claimOverviewLoader,
        },
        {
          id: 'register',
          path: 'register',
          loader: makeRegistrationLoader(({ context, params }) =>
            context.settings.registrations.isEnabled ?
              {
                ok: true,
              }
            : redirect(`/${params.store ?? ''}`),
          ).loader,
          children: [
            {
              id: 'customerInfo',
              action: customerInfoAction,
              loader: customerInfoLoader,
              Component: CustomerInfoPage,
              index: true,
            },
            {
              id: 'selectProducts',
              path: 'product',
              action: selectProductsAction,
              loader: selectProductsLoader,
              Component: SelectProductsPage,
            },
            {
              id: 'registrationOverview',
              path: 'overview',
              Component: RegistrationOverviewPage,
              loader: registrationLoader,
            },
          ],
        },
        catchallRoute,
      ],
    },
    // Shipping Protection routes
    {
      path: '/:store?/reorder/:idFromPlatform',
      handle: {
        middleware: protectionOrderMiddleware,
      },
      children: [
        {
          index: true,
          Component: ProtectionOrderOverviewPage,
          loader: protectionOrderOverviewLoader,
        },
        {
          id: 'protectionOrderNotProtected',
          path: 'not-protected',
          Component: ProtectionOrderNotProtectedPage,
        },
        {
          id: 'shippingProtectionClaimOverview',
          path: 'claim/:claimId/:entryPoint?',
          Component: ShippingProtectionClaimOverviewPage,
          loader: shippingProtectionClaimOverviewLoader,
        },
        {
          path: 'new',
          children: [
            {
              id: 'shippingClaimReason',
              index: true,
              Component: ShippingClaimReasonPage,
              loader: shippingClaimReasonLoader,
              action: shippingClaimReasonAction,
            },
            {
              id: 'shippingIssue',
              path: 'shipping-issue',
              Component: ShippingIssuePage,
              loader: shippingIssueLoader,
            },
            {
              id: 'shippingClaimItems',
              path: 'items',
              Component: ShippingClaimItemsPage,
              loader: shippingClaimItemsLoader,
              action: shippingClaimItemsAction,
            },
            {
              id: 'shippingClaimMeta',
              path: 'meta',
              Component: ShippingClaimMetaPage,
              loader: shippingClaimMetaLoader,
              action: shippingClaimMetaAction,
            },
            {
              id: 'shippingClaimAddressReview',
              path: 'address',
              Component: AddressReviewPage,
              loader: addressReviewLoader,
            },
            {
              id: 'shippingClaimAddressEdit',
              path: 'address/edit',
              Component: EditAddressPage,
              action: editAddressAction,
              loader: editAddressLoader,
            },
            {
              path: 'review',
              Component: ShippingClaimReviewPage,
              loader: shippingClaimReviewLoader,
              action: shippingClaimReviewAction,
              // don't want to run loader again with action submit since we are creating a claim and clearing the context
              shouldRevalidate: () => false,
            },
          ],
        },
      ],
    },
    catchallRoute,
  ],
} satisfies RouteObject;
